import type { ButtonStyle, ButtonVariant } from '@liveauctioneers/hammer-ui-core/types';

import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Bell, Check } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useRef } from 'react';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { modalTypes } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    CompleteAccountModalContext,
    dismissModal,
    openSimilarItemAlertFeedbackModal,
    SIMILAR_ITEM_ALERT_FEEDBACK_DELAY_MS,
} from '@/redux/modules/modal';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import {
    fetchSimilarItemAlert,
    removeSimilarItemAlert,
    submitSimilarItemAlert,
} from '@/redux/modules/similarItemAlerts/similarItemAlerts.actions';
import {
    getIsSimilarItemAlertSet,
    getSimilarItemAlertId,
} from '@/redux/modules/similarItemAlerts/similarItemAlerts.selectors';
import {
    SimilarItemAlertSurface,
    useTrackSimilarItemAlertRemoved,
    useTrackSimilarItemAlertSet,
} from '@/utils/amplitude/analytics/similarItems/trackSimilarItemAlert.analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getForYouUrl } from '@/utils/urls';

type Props = {
    className?: string;
    isPriceResultsRedesign?: boolean;
    itemId: number;
    style?: ButtonStyle;
    surface?: SimilarItemAlertSurface;
    variant?: ButtonVariant;
};

/**
 * The Similar Item Alert toggle, rendered on the ended-lot PDP and on Price Results.
 *
 * On the PDP it goes into hammer-ui's `BidCanvasVariations.similarItemAlertComponent` slot, which
 * only mounts it where "Sell a Similar Item" shows — i.e. an ended lot. That slot is what scopes
 * the CTA to lots a bidder can no longer bid on, so it needs no ended-lot check of its own.
 */
export const SimilarItemAlertCTA = ({
    className = 'mt-xs w-full',
    isPriceResultsRedesign = false,
    itemId,
    style = 'primary',
    surface = SimilarItemAlertSurface.PDP,
    variant = 'transparent',
}: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const verifyLogin = useRequireLogin();

    const userIsLoggedIn = useAppSelector(isUserLoggedIn);
    const isAlertSet = useAppSelector((state) => getIsSimilarItemAlertSet(state, itemId));
    const savedItemId = useAppSelector((state) => getSimilarItemAlertId(state, itemId));
    const lotTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const openModal = useAppSelector(getOpenModal);

    const trackAlertSet = useTrackSimilarItemAlertSet();
    const trackAlertRemoved = useTrackSimilarItemAlertRemoved();

    // Holds the pending "open the feedback modal" timer so Undo can cancel it before it fires.
    const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(
        () => () => {
            clearTimeout(feedbackTimerRef.current);
        },
        []
    );

    // Read through a ref so the Undo handler captured in the toast (created while no modal was open)
    // still sees the *current* open modal when it's clicked a few seconds later.
    const openModalRef = useRef(openModal);
    openModalRef.current = openModal;

    // The check endpoint is JWT-only, so a logged-out bidder just sees the OFF state until they
    // sign in.
    useEffect(() => {
        if (userIsLoggedIn) {
            dispatch(fetchSimilarItemAlert({ lotId: itemId }));
        }
    }, [dispatch, itemId, userIsLoggedIn]);

    // These read the dispatched action rather than unwrapping it: a rejected thunk would throw out
    // of the click handler as an unhandled rejection. A failed request leaves the CTA on its
    // current state and shows no toast, which is the honest thing to show until the feature has a
    // designed error state.
    const setAlert = useCallback(async () => {
        const action = await dispatch(submitSimilarItemAlert({ lotId: itemId, lotTitle }));
        if (!submitSimilarItemAlert.fulfilled.match(action)) {
            return false;
        }
        trackAlertSet({ itemId, surface });
        return true;
    }, [dispatch, itemId, lotTitle, surface, trackAlertSet]);

    const handleSetAlert = useCallback(async () => {
        if (!(await setAlert())) {
            return;
        }
        toast('success', {
            description: (
                <>
                    {formatMessage(['similarItemAlerts', 'alertCreatedDescription'])}{' '}
                    <RouteLink to={getForYouUrl()}>{formatMessage(['similarItemAlerts', 'manageAlerts'])}</RouteLink>
                </>
            ),
            title: String(formatMessage(['similarItemAlerts', 'alertCreatedTitle'])),
        });
    }, [formatMessage, setAlert]);

    // Undo re-creates the alert AND cancels the feedback prompt — the removal it was collecting
    // feedback about didn't stick. Cancels the pending timer if the modal hasn't opened yet, and
    // closes it if it already has (its own close path fires no event on a redux-driven dismiss).
    const handleUndo = useCallback(async () => {
        clearTimeout(feedbackTimerRef.current);
        if (openModalRef.current === modalTypes.SIMILAR_ITEM_ALERT_FEEDBACK) {
            dispatch(dismissModal());
        }
        await setAlert();
    }, [dispatch, setAlert]);

    const handleRemoveAlert = useCallback(async () => {
        const action = await dispatch(removeSimilarItemAlert({ lotId: itemId, savedItemId }));
        if (!removeSimilarItemAlert.fulfilled.match(action)) {
            return;
        }
        trackAlertRemoved({ itemId, surface });
        toast('success', {
            // Undo re-creates the alert, so it comes back with a new savedItemId. That's expected —
            // the row is gone, this is not a restore.
            description: (
                <InlineButton onClick={handleUndo}>{formatMessage(['similarItemAlerts', 'undo'])}</InlineButton>
            ),
            title: String(formatMessage(['similarItemAlerts', 'alertRemovedTitle'])),
        });
        // The feedback modal follows the toast by a beat (SEAR-1647), unless Undo cancels it.
        feedbackTimerRef.current = setTimeout(() => {
            dispatch(openSimilarItemAlertFeedbackModal({ itemId }));
        }, SIMILAR_ITEM_ALERT_FEEDBACK_DELAY_MS);
    }, [dispatch, formatMessage, handleUndo, itemId, savedItemId, surface, trackAlertRemoved]);

    const handleClick = useCallback(async () => {
        await verifyLogin({
            modalContext: { id: itemId, type: CompleteAccountModalContext.SimilarItemAlert },
            requireLogin: true,
            runFunction: isAlertSet ? handleRemoveAlert : handleSetAlert,
        });
    }, [handleRemoveAlert, handleSetAlert, isAlertSet, itemId, verifyLogin]);

    return (
        <Button
            // Button is built for short labels: it forces `nowrap` and a single-line `leading`, so
            // below ~420px this copy overflows its own padding and the icon lands outside the
            // button. Allow wrapping on mobile rather than shortening the approved copy — the
            // label still sits on one line wherever it fits, so only the narrow case changes.
            // Price results redesign copy fits the default 42px height, so leave that override off.
            className={cn(
                className,
                !isPriceResultsRedesign && 'mobile:h-auto mobile:leading-normal mobile:whitespace-normal'
            )}
            iconLeft={isAlertSet ? Check : Bell}
            onClick={handleClick}
            style={style}
            variant={variant}
        >
            {isAlertSet
                ? formatMessage(['similarItemAlerts', 'alertsAreOn'])
                : formatMessage(['similarItemAlerts', isPriceResultsRedesign ? 'notifyMeAbout' : 'notifyMe'])}
        </Button>
    );
};
